import { Injectable, computed, signal } from '@angular/core';

/** A job handed out by `begin()`, given back to `relabel()` and `end()`. */
export type BusyJob = number;

interface Job {
  id: BusyJob;
  label: string;
}

/**
 * BUSY SERVICE
 * ------------
 * One app-wide answer to "is something being saved right now, and what?".
 * Rendered by the pill in the app shell, so any part of the app gets the same
 * indicator without owning one:
 *
 *   await this.busy.track('Saving changes', () => this.data.saveMatchDetails(...));
 *
 * Jobs are counted rather than held as a boolean: the panel runs overlapping
 * writes — a photo upload going while a fixture is saved — and a boolean would
 * clear on whichever finished first and leave the rest looking idle.
 *
 * Each job is identified by a handle rather than by its label, because the
 * gallery runs several uploads at once under labels that keep changing as the
 * count climbs. Matching on the text would let two workers race for the same
 * entry and strand it, leaving the pill up for good.
 *
 * Two pieces of timing keep it from being noise on a good connection:
 *
 *  - Nothing shows for the first SHOW_AFTER_MS. Most Supabase calls come back
 *    inside that, and a pill that appears and vanishes in 80ms reads as a
 *    glitch rather than as progress.
 *  - Once shown it stays for at least MIN_VISIBLE_MS, so it cannot flicker.
 *
 * Only for work somebody started by tapping something. Background refreshes
 * from Realtime must not come through here: another scorer saving a goal would
 * otherwise light up everyone else's screen.
 */
@Injectable({ providedIn: 'root' })
export class BusyService {

  /** Long enough that a quick save stays invisible, short enough to feel prompt. */
  private static readonly SHOW_AFTER_MS = 250;
  /** Once it is on screen, how long it must stay to avoid a flicker. */
  private static readonly MIN_VISIBLE_MS = 400;

  /** Jobs in flight, oldest first. Empty means idle. */
  private readonly jobs = signal<Job[]>([]);
  private readonly visible = signal(false);

  private nextId = 1;
  private showTimer?: ReturnType<typeof setTimeout>;
  private hideTimer?: ReturnType<typeof setTimeout>;
  private shownAt = 0;

  /**
   * The text the pill is showing, or null when there is nothing to say.
   *
   * Deliberately not derived from `jobs` alone: during the minimum-visible
   * hold the last job has already gone, and reading the label straight off an
   * empty list would blank the pill at exactly the moment the hold exists to
   * prevent. The last thing said is kept until the pill is taken down.
   */
  private readonly held = signal<string | null>(null);
  readonly label = computed<string | null>(() => (this.visible() ? this.held() : null));

  /** The newest job wins: it is the one they just asked for. */
  private syncLabel(): void {
    const jobs = this.jobs();
    if (jobs.length) this.held.set(jobs[jobs.length - 1].label);
  }

  /** True whenever work is in flight, whether or not the pill is showing yet. */
  readonly active = computed(() => this.jobs().length > 0);

  /**
   * Runs `work` with `label` on screen, and returns whatever it returns.
   * The job is cleared however the work ends, so a thrown error cannot strand
   * the indicator: callers keep their own try/catch for the message.
   */
  async track<T>(label: string, work: () => Promise<T>): Promise<T> {
    const job = this.begin(label);
    try {
      return await work();
    } finally {
      this.end(job);
    }
  }

  /** For work whose start and end are not one call, such as the photo queue. */
  begin(label: string): BusyJob {
    const id = this.nextId++;
    this.jobs.update(jobs => [...jobs, { id, label }]);
    this.syncLabel();

    if (!this.visible() && !this.showTimer) {
      this.showTimer = setTimeout(() => {
        this.showTimer = undefined;
        // The work may have finished during the wait, which is the whole point.
        if (!this.jobs().length) return;
        this.shownAt = Date.now();
        this.visible.set(true);
      }, BusyService.SHOW_AFTER_MS);
    }
    return id;
  }

  /** Ends one job; any others still running keep the pill up. */
  end(job: BusyJob): void {
    this.jobs.update(jobs => jobs.filter(j => j.id !== job));
    if (this.jobs().length) {
      this.syncLabel();
      return;
    }

    clearTimeout(this.showTimer);
    this.showTimer = undefined;

    if (!this.visible()) return;

    const remaining = BusyService.MIN_VISIBLE_MS - (Date.now() - this.shownAt);
    if (remaining <= 0) {
      this.visible.set(false);
      return;
    }
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.hideTimer = undefined;
      // More work may have started while we were holding it open.
      if (!this.jobs().length) this.visible.set(false);
    }, remaining);
  }

  /**
   * Renames a running job, for work that reports its own progress
   * ("Uploading 3 of 12"). A no-op once the job has ended.
   */
  relabel(job: BusyJob, label: string): void {
    this.jobs.update(jobs => jobs.map(j => (j.id === job ? { ...j, label } : j)));
    this.syncLabel();
  }
}
